import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import moment, { Moment } from 'moment'
import { DatePicker } from '@material-ui/pickers'
import { FormControl, InputLabel } from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'
import Checkbox from '@material-ui/core/Checkbox'
import Divider from '@material-ui/core/Divider'
import FormControlLabel from '@material-ui/core/FormControlLabel'

import { Dialog, UploadButton, snackbar, Notice } from 'components'
import { useApiCall, useCheckFormErrors, useApi } from 'hooks'
import { STRINGS } from 'consts'
import { Tag } from 'features/Main/Tags/types'
import { Store } from 'features/Main/Stores/types'
import { getStores } from 'features/Main/Stores/api'
import { resizeFile } from 'utils'
import { PrizeCategory } from 'consts/prize'

import { Coupon, UpdateCouponResponse, UpdateCouponData } from '../../types'
import { updateCoupon } from '../../api'
import { useStyles } from './styles'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  accentDescription: [
    {
      validate: (value: string) => value.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string | null) => value === null || value.length <= 20,
      message: 'No puede superar los 20 caracteres',
    },
  ],
  description: [
    {
      validate: (value: string) => value.length <= 254,
      message: 'No puede superar los 254 caracteres',
    },
  ],
  expiryDate: [
    {
      validate: (value: Moment | null) => !!value && value.isSameOrAfter(moment().startOf('day')),
      message: 'La fecha de vencimiento no puede ser anterior a hoy',
    },
  ],
  order: [
    {
      validate: (value: string) => (value && Number(value) > 0) || !value,
      message: 'El orden debe ser mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: any) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID Beneficio Toteat es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: any) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
  applicableDays: [
    {
      validate: (days: string[]) => days.length > 0,
      message: 'Debe seleccionar al menos un día',
      validateEmpty: true,
    },
  ],
}

interface UpdateCouponDialogProps {
  handleClose: () => void
  couponToUpdate: Coupon
  onDone: (newCoupon: Coupon) => void
  tags: Tag[]
  calcUsersByTags: (tagsSelected: any[], allTagsAreRequired?: boolean) => Promise<number>
}

interface StoreOption {
  label: string
  value: number
}

const UpdateCouponDialog: React.FC<UpdateCouponDialogProps> = ({
  handleClose,
  onDone,
  couponToUpdate,
  tags,
  calcUsersByTags,
}) => {
  const classes = useStyles()

  const [{ data: stores, isLoading: isLoadingStores }] = useApi<{ stores: Store[] }, Store[]>(
    getStores,
    response => response.stores,
    { baseData: [] },
  )

  const [toteatBenefitId] = useState<string>(couponToUpdate.toteatBenefitId || '')
  const [skusList] = useState<string[]>(
    couponToUpdate.skus ? couponToUpdate.skus.map(s => s.sku) : [],
  )
  const [storesEnabled] = useState(couponToUpdate.storesEnabled)
  const [tucanEnabled] = useState(couponToUpdate.tucanEnabled)
  const [toteatEnabled] = useState(couponToUpdate.toteatEnabled)
  const [category] = useState<PrizeCategory | undefined>(couponToUpdate.category)
  const [title, setTitle] = useState(couponToUpdate.title)
  const [accentDescription, setAccentDescription] = useState(couponToUpdate.accentDescription)
  const [additionalText, setAdditionalText] = useState(couponToUpdate.additionalText)
  const [description, setDescription] = useState(couponToUpdate.description ?? '')
  const [image, setImage] = useState<File | undefined>(undefined)
  const [banner, setBanner] = useState<File | undefined>(undefined)
  const [imagePreview, setImagePreview] = useState(couponToUpdate.imageUrl)
  const [bannerPreview, setBannerPreview] = useState(couponToUpdate.bannerUrl)
  const [icon, setIcon] = useState<File | undefined>(undefined)
  const [iconPreview, setIconPreview] = useState(couponToUpdate.iconUrl)
  const [expiryDate, setExpiryDate] = useState<Moment | null>(moment(couponToUpdate.expiryDate))
  const [order, setOrder] = useState(couponToUpdate.order?.toString() ?? '')
  const [tagsSelected, setTagsSelected] = useState<any[]>([])
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(couponToUpdate.allTagsAreRequired)
  const [usersByTags, setUsersByTags] = useState(0)
  const [applicableDays, setApplicableDays] = useState<string[]>(() => {
    if (!couponToUpdate.applicable_days) return []
    // Si ya es un array, usarlo directamente
    if (Array.isArray(couponToUpdate.applicable_days)) {
      return couponToUpdate.applicable_days
    }
    // Si es string, intentar parsearlo como JSON
    if (typeof couponToUpdate.applicable_days === 'string') {
      try {
        const parsed = JSON.parse(couponToUpdate.applicable_days)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        // Si no es JSON válido, intentar como string separado por comas
        return couponToUpdate.applicable_days
          .split(',')
          .map(day => day.trim().toLowerCase())
          .filter(Boolean)
      }
    }
    return []
  })

  const daysOfWeek = [
    { label: 'Lunes', value: 'monday' },
    { label: 'Martes', value: 'tuesday' },
    { label: 'Miércoles', value: 'wednesday' },
    { label: 'Jueves', value: 'thursday' },
    { label: 'Viernes', value: 'friday' },
    { label: 'Sábado', value: 'saturday' },
    { label: 'Domingo', value: 'sunday' },
  ]
  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const [storesSelected, setStoresSelected] = useState<StoreOption[]>([])

  useEffect(() => {
    if (couponToUpdate.stores) {
      const initialStores = couponToUpdate.stores.map(store => ({
        label: store.name,
        value: store.id,
      }))
      setStoresSelected(initialStores)
    }
  }, [couponToUpdate])

  useEffect(() => {
    if (!couponToUpdate || !couponToUpdate.tags) return

    const localTagsSelected = couponToUpdate.tags.map(el => ({ label: el.title, value: el.id }))
    setTagsSelected(localTagsSelected)
  }, [couponToUpdate])

  useEffect(() => {
    calcUsersByTags(tagsSelected, allTagsAreRequired).then(el => {
      setUsersByTags(el)
    })
  }, [allTagsAreRequired, tagsSelected])

  const handleSetTags = async (e: any[]) => {
    setTagsSelected(e)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  const [updateCouponApi, isLoading] = useApiCall<UpdateCouponData, UpdateCouponResponse>(
    updateCoupon,
  )

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setTitle(e.target.value)
  }

  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setDescription(e.target.value)
  }

  const handleAccentDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAccentDescription(e.target.value)
  }

  const handleAdditionalTextChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAdditionalText(e.target.value)
  }

  const handleDateChange = (newDate: Moment | null) => {
    setExpiryDate(newDate)
  }

  const handleImageChange = async (file: File) => {
    setImagePreview(URL.createObjectURL(file))
    const resizedImage = await resizeFile(file, 600, 600)
    setImage(resizedImage)
  }

  const handleBannerChange = async (file: File) => {
    setBannerPreview(URL.createObjectURL(file))
    const resizedImage = await resizeFile(file, 600, 600)
    setBanner(resizedImage)
  }

  const handleIconChange = async (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    const resizedIcon = await resizeFile(file, 192, 192)
    setIcon(resizedIcon)
  }

  const handleOrderChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setOrder(e.target.value)
  }

  const getRequiredFields = () => {
    const requiredFields = [
      'title',
      'accentDescription',
      'image',
      'icon',
      'banner',
      'expiryDate',
      'applicableDays',
    ]

    if (toteatEnabled) {
      if (category === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (category === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    accentDescription,
    description,
    expiryDate,
    order,
    additionalText,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    category,
    applicableDays,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading

  const storesForSelect = stores.map(store => ({ label: store.name, value: store.id }))

  const onUpdateCoupon = async () => {
    const coupon = {
      title,
      accentDescription,
      description,
      image,
      icon,
      banner,
      expiryDate: expiryDate?.toISOString(),
      order,
      additionalText,
      tagsSelected,
      allTagsAreRequired,
      category: category || '',
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
      applicable_days: applicableDays,
      storeIds: storesSelected.map(store => store.value).join(','),
    }
    const payload = {
      id: couponToUpdate.id,
      payload: coupon,
    }
    try {
      const { coupon: updatedCoupon } = await updateCouponApi(payload)
      onDone(updatedCoupon)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo editar la promo. Intente de nuevo.')
    }
  }

  return (
    <Dialog
      title="Editar promo"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateCoupon}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.title.hasError}
            helperText={errors.title.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripción corta"
            value={accentDescription}
            fullWidth
            onChange={handleAccentDescriptionChange}
            disabled={isLoading}
            required
            error={errors.accentDescription.hasError}
            helperText={errors.accentDescription.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto adicional"
            value={additionalText}
            fullWidth
            onChange={handleAdditionalTextChange}
            disabled={isLoading}
            error={errors.additionalText.hasError}
            helperText={errors.additionalText.message}
            inputProps={{ maxLength: 20 }}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.multiselectTitle}>Días de la semana</InputLabel>
          <FormControl className={classes.multiselect}>
            <MultiSelect
              options={daysOfWeek}
              value={daysOfWeek.filter(day => applicableDays.includes(day.value))}
              onChange={(selected: { label: string; value: string }[]) =>
                setApplicableDays(selected.map(day => day.value))
              }
              labelledBy="Seleccionar días"
              hasSelectAll={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripción larga"
            value={description}
            fullWidth
            onChange={handleDescriptionChange}
            disabled={isLoading}
            multiline
            error={errors.description.hasError}
            helperText={errors.description.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            value={expiryDate}
            onChange={handleDateChange}
            autoOk
            disablePast
            format={STRINGS.DATE_FORMAT}
            initialFocusedDate={new Date()}
            disabled={isLoading}
            style={{ width: '100%' }}
            label="Vencimiento"
            minDateMessage="La fecha de vencimiento no puede ser anterior a hoy"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={order}
            onChange={handleOrderChange}
            label="Order"
            type="number"
            fullWidth
            error={errors.order.hasError}
            helperText={errors.order.message}
            InputProps={{
              inputProps: { min: 1 },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.multiselectTitle}>Tag</InputLabel>
          <FormControl className={classes.multiselect}>
            <MultiSelect
              options={tagsForSelect}
              value={tagsSelected}
              onChange={handleSetTags}
              labelledBy="Seleccionar Tags"
              hasSelectAll={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={11}>
          <FormControlLabel
            control={
              <Checkbox onChange={handleAllTagsAreRequiredChange} checked={allTagsAreRequired} />
            }
            label="Sólo usuarios con todos los tags seleccionados"
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.multiselectTitle}>Tiendas</InputLabel>
          <FormControl className={classes.multiselect}>
            <MultiSelect
              options={storesForSelect}
              value={storesSelected}
              onChange={(selected: StoreOption[]) => setStoresSelected(selected)}
              labelledBy="Seleccionar tiendas"
              hasSelectAll={false}
              disabled={isLoadingStores}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={12}>
          <div className={classes.iconUploadContainer}>
            <UploadButton
              id="coupon-icon-upload-button"
              accept=".jpg, .jpeg, .png"
              label="Subir ícono"
              onChange={handleIconChange}
            />
            <div className={classes.iconUploadPreviewContainer}>
              <img src={iconPreview} alt="" className={classes.iconPreview} />
            </div>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.imageUploadContainer}>
            <UploadButton
              id="coupon-banner-upload-button"
              accept=".jpg, .jpeg, .png"
              label="Subir banner"
              onChange={handleBannerChange}
            />
            <div className={classes.imageUploadBannerPreviewContainer}>
              <img src={bannerPreview} alt="" className={classes.imagePreview} />
            </div>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.imageUploadContainer}>
            <UploadButton
              id="coupon-image-upload-button"
              accept=".jpg, .jpeg, .png"
              label="Subir imagen"
              onChange={handleImageChange}
            />
            <div className={classes.imageUploadPreviewContainer}>
              <img src={imagePreview} alt="" className={classes.imagePreview} />
            </div>
          </div>
        </Grid>
        <Grid item xs={11}>
          <Divider variant="middle" />
        </Grid>
        <Grid item xs={12}>
          <Notice>Esta acción impactará a {usersByTags} usuarios</Notice>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UpdateCouponDialog }
